import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import Login from './pages/Login';
import GoogleCallback from './pages/GoogleCallback';
import LandingPage from './pages/LandingPage';
import LegalPage from './pages/LegalPage';
import GuardDashboard from './pages/GuardDashboard';
import ResidentDashboard from './pages/ResidentDashboard';
import ManagementDashboard from './pages/ManagementDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ProfilePage from './pages/ProfilePage';
import { PushNotificationManager } from './components/PushNotificationManager';
import { getAuthToken, getStoredUser } from './services/auth';
import { CommunityConfigProvider } from './services/communityConfig';
import { TCGate } from './components/legal/TCGate';
import { unlockAudio, playNotificationChime } from './utils/notificationSound';
import { Toaster } from './components/ui/sonner';

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(() => {})
      .catch(() => {});
  });

  // Unlock audio on first user interaction (iOS requirement)
  document.addEventListener('touchstart', unlockAudio, { once: true });
  document.addEventListener('click', unlockAudio, { once: true });

  // Play chime when push received in foreground or notification tapped
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
      playNotificationChime();
    }
  });
}

const getToken = () => getAuthToken();
const getUser = () => getStoredUser();

/**
 * Prevent Android back button from navigating to the login page
 * while authenticated. In standalone PWA mode, pushes a guard
 * history entry so the back button keeps the user on their dashboard.
 */
function BackButtonGuard() {
  const location = useLocation();

  useEffect(() => {
    const dashRoutes = ['/guard', '/resident', '/management', '/super-admin', '/profile'];
    if (!dashRoutes.includes(location.pathname)) return;
    if (!getToken()) return;

    // Push two extra history entries to create a buffer
    // Back press 1: pops to buffer → we re-push (app stays)
    // Back press 2 (rapid): pops to buffer → we re-push (app stays)
    // This prevents navigating back to login
    window.history.pushState({ gk: true }, '', location.pathname);
    window.history.pushState({ gk: true }, '', location.pathname);

    const handlePopState = () => {
      if (getToken()) {
        // Always re-push to keep the user on the current dashboard
        window.history.pushState({ gk: true }, '', location.pathname);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);

  return null;
}

const PrivateRoute = ({ children }) => {
  const token = getToken();
  return token ? (
    <CommunityConfigProvider>
      <TCGate>{children}</TCGate>
    </CommunityConfigProvider>
  ) : (
    <Navigate to="/login" />
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BackButtonGuard />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/google-callback" element={<GoogleCallback />} />
          <Route
            path="/guard"
            element={
              <PrivateRoute>
                <GuardDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/resident"
            element={
              <PrivateRoute>
                <ResidentDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/management"
            element={
              <PrivateRoute>
                <ManagementDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <PrivateRoute>
                <SuperAdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
        </Routes>
        <PushNotificationManager />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;